import React from 'react';
import styled from 'styled-components';
import { Text } from '../atoms/text';

// Types
interface MessageBubbleProps {
  type: 'user' | 'gpt' | 'system' | 'expert' | 'widget-upload' | 'video-call';
  content: string | React.ReactNode;
  timestamp: string;
  onClick?: () => void;
  senderName?: string;
}

type BubbleType = MessageBubbleProps['type'];

// One look per sender, in the website palette (imavenai.com): the client's own messages use the
// site's blue -> indigo gradient, the adviser a white card, the AI assistant violet (AI = violet on
// the site), system notes a quiet dashed panel, calls and uploads cyan.
interface BubbleStyle { bg: string; border: string; text: string; name: string; time: string; shadow: string; hover?: string; dashed?: boolean }
const BUBBLE_STYLES: Record<string, BubbleStyle> = {
  user: { bg: 'linear-gradient(112deg, #2c58e9, #6b61e5)', border: 'transparent', text: '#ffffff', name: 'rgba(255, 255, 255, 0.9)', time: 'rgba(255, 255, 255, 0.72)', shadow: '0 6px 16px rgba(70, 92, 213, 0.16)' },
  expert: { bg: '#ffffff', border: '#e0e8f5', text: '#33445e', name: '#3359c7', time: '#8f9db3', shadow: '0 3px 12px rgba(37, 65, 122, 0.05)' },
  gpt: { bg: '#f7f5ff', border: '#e6e0fb', text: '#34405c', name: '#7955cd', time: '#9b95bd', shadow: '0 3px 12px rgba(121, 85, 205, 0.05)' },
  system: { bg: '#f5f8fd', border: '#d5dff1', text: '#5d6f89', name: '#65748d', time: '#97a5ba', shadow: 'none', dashed: true },
  'widget-upload': { bg: '#f2fbff', border: '#cdeefc', text: '#33445e', name: '#137ca9', time: '#8fa9b8', shadow: '0 3px 12px rgba(19, 124, 169, 0.05)' },
  'video-call': { bg: '#f2fbff', border: '#cdeefc', text: '#33445e', name: '#137ca9', time: '#8fa9b8', shadow: '0 3px 12px rgba(19, 124, 169, 0.05)', hover: '#e6f6fe' },
};
const bubbleStyle = (type: BubbleType): BubbleStyle => BUBBLE_STYLES[type] || BUBBLE_STYLES.expert;

// Styled Components
const Container = styled.div<{ type: MessageBubbleProps['type'] }>`
  display: flex;
  justify-content: ${(props) => (props.type === 'user' ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  margin-bottom: 10px;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const Bubble = styled.div<{ type: MessageBubbleProps['type'] }>`
  background: ${(props) => bubbleStyle(props.type).bg};
  color: ${(props) => bubbleStyle(props.type).text};
  padding: 12px 16px;
  border-radius: 14px;
  /* "tail" corner on the sender's side */
  ${(props) => (props.type === 'user' ? 'border-bottom-right-radius: 4px;' : 'border-top-left-radius: 4px;')}
  display: inline-block;
  width: max-content; /* grow to longest line */
  box-sizing: border-box;
  margin-left: ${(props) => (props.type === 'user' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'user' ? '0' : '10px')};
  word-wrap: break-word;
  overflow-wrap: break-word; /* avoid premature wrapping that shrinks intrinsic width */
  white-space: normal; /* let children control wrapping */
  overflow: hidden; /* clip content to rounded corners */
  line-height: 1.5;
  position: relative;
  font-size: 14px;
  z-index: 0;
  text-align: left;
  border: 1px ${(props) => (bubbleStyle(props.type).dashed ? 'dashed' : 'solid')} ${(props) => bubbleStyle(props.type).border};
  box-shadow: ${(props) => bubbleStyle(props.type).shadow};
  cursor: ${(props) => (props.type === 'video-call' ? 'pointer' : 'default')};
  transition: ${(props) => (props.type === 'video-call' ? 'background 0.2s' : 'none')};

  &:hover {
    background: ${(props) => bubbleStyle(props.type).hover || bubbleStyle(props.type).bg};
  }

  /* links and file names inside the client's own (blue) bubble */
  ${(props) => props.type === 'user' && `
    && a, && a:hover, && a:visited { color: #ffffff; }
  `}
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`;

const Timestamp = styled(Text)<{ $type: BubbleType }>`
  font-size: 10px;
  color: ${(props) => bubbleStyle(props.$type).time};
  margin-top: 5px;
  white-space: normal;
  overflow-wrap: anywhere;
  
  @media (max-width: 480px) {
    font-size: 9px;
    text-align: right;
  }
`;

const Content = styled(Text)<{ $type: BubbleType }>`
  color: ${(props) => bubbleStyle(props.$type).text};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line; /* treat single \n as break, but allow long lines to stay inline */
  word-break: break-word;
`;

const SenderLine = styled.div<{ type: MessageBubbleProps['type'] }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => bubbleStyle(props.type).name};
  margin: 0 0 2px 0; /* tighter so it doesn't create extra line wrap */
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;
`;

// Wrapper for HTML content to normalize spacing and margins
const HtmlContent = styled.div<{ $type: BubbleType }>`
  color: ${(props) => bubbleStyle(props.$type).text};
  white-space: pre-wrap; /* respect line breaks in plain text converted to HTML */
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
  text-align: left;

  & p,
  & div,
  & br {
    margin: 0 0 6px 0;
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0 0 8px 0;
    font-size: inherit; /* avoid oversized headings in bubbles */
    font-weight: 600;
  }

  & ul,
  & ol {
    margin: 6px 0 6px 18px;
    padding-left: 18px;
  }

  & li {
    margin: 0 0 2px 0;
  }

  & a {
    color: ${(props) => (props.$type === 'user' ? '#ffffff' : '#2c64da')};
    text-decoration: ${(props) => (props.$type === 'user' ? 'underline' : 'none')};
  }

  & a:hover {
    text-decoration: underline;
  }

  & strong {
    font-weight: 600;
  }

  /* Table styling */
  & table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 12px;
  }

  & th,
  & td {
    padding: 6px 10px;
    border: 1px solid #e0e8f5;
    text-align: left;
  }

  & th {
    background: #edf2ff;
    font-weight: 600;
  }

  & tr:nth-child(even) {
    background: #f5f8fd;
  }

  /* Horizontal rule */
  & hr {
    border: none;
    border-top: 1px solid #e0e8f5;
    margin: 10px 0;
  }

  @media (max-width: 768px) {
    line-height: 1.5;
    & p,
    & div {
      margin-bottom: 8px;
    }
    & ul,
    & ol {
      margin-left: 16px;
    }
  }

  @media (max-width: 480px) {
    line-height: 1.55;
    & p,
    & div {
      margin-bottom: 10px;
    }
  }
`;

export const MessageBubble: React.FC<MessageBubbleProps> = ({ type, content, timestamp, onClick, senderName }) => {
  const isStringContent = typeof content === 'string' || typeof content === 'number';
  return (
    <Container type={type}>
      <Bubble type={type} onClick={onClick}>
        {senderName && <SenderLine type={type}>{senderName}</SenderLine>}
        {isStringContent ? (
          <Content as="p" $type={type}>{content}</Content>
        ) : (
          <HtmlContent $type={type}>{content}</HtmlContent>
        )}
        <Timestamp $type={type}>
          {new Date(timestamp).toLocaleString([], {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Timestamp>
      </Bubble>
    </Container>
  );
};
